/**
 * The order of the offscreen viewport passes. One `useFrame` orchestrator drives
 * them all, where Godot walks the viewport tree bottom-up, so a pass that samples
 * another's target (a `ViewportTexture`, often on a nested `SubViewport`) runs after it.
 */
/**
 * Depth-first post-order over `dependsOn` edges, with white/gray/black cycle
 * detection. A cycle has no valid order, so the closing edge is dropped, not the
 * pass: its other dependencies still order, and the cycle is reported with the
 * sampler that closed it, so a caller can skip or warn about that pass alone.
 */
export function orderViewportPasses(passes) {
    const dependsOn = new Map();
    for (const pass of passes)
        dependsOn.set(pass.id, pass.dependsOn);
    const state = new Map();
    const stack = [];
    const order = [];
    const cycles = [];
    function visit(id) {
        if (state.get(id) === 'done')
            return;
        state.set(id, 'visiting');
        stack.push(id);
        for (const dep of dependsOn.get(id) ?? []) {
            if (!dependsOn.has(dep))
                continue; // A dangling reference has nothing to order against.
            if (state.get(dep) === 'visiting') {
                const cycleStart = stack.indexOf(dep);
                cycles.push({ sampler: id, path: [...stack.slice(cycleStart), dep] });
                continue; // Break the closing edge. This pass's other dependencies still order.
            }
            visit(dep);
        }
        stack.pop();
        state.set(id, 'done');
        order.push(id);
    }
    for (const pass of passes)
        visit(pass.id);
    return { order, cycles };
}
//# sourceMappingURL=passOrder.js.map